import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import {
  CreateDataSourceRequestBody,
  ListDataSourceRequestQuery,
  ListDataSourceResponse,
} from "common";
import { ErrorNotification } from "@via/utils/interfaces.tsx";

export const fetchDataSourceList = async (
  params: ListDataSourceRequestQuery
): Promise<ListDataSourceResponse> => {
  try {
    const response = await axios.get("/api/data-source/v1/", { params });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetDataSourceList = (
  search?: string
): UseQueryResult<ListDataSourceResponse, Error> => {
  return useQuery({
    queryKey: ["/api/data-source/v1/", search],
    queryFn: async () => await fetchDataSourceList({ search }),
    retry: true,
  });
};

export const useCreateDataSource = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: CreateDataSourceRequestBody) => {
      const res = await axios.post("/api/data-source/v1/", payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "create-data-source",
        message: "Item Successfully Created",
        details: "Item has been created",
        variant: NOTIFICATION.SUCCESS,
      });
      // invalidate listing query
      queryClient.invalidateQueries({
        queryKey: ["/api/data-source/v1/"],
        refetchType: "all",
      });
    },
    onError: error => {
      setNotification({
        id: "create-data-source-failed",
        message: "Item Creation Failed",
        details:
          (error as { response?: { data?: { message?: string } } }).response
            ?.data?.message ?? "Item could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteDataSource = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.delete(`/api/data-source/v1/${id}`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "delete-data-source",
        message: "Item Successfully Deleted",
        details: "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the Data Source list
      queryClient.invalidateQueries({
        predicate: query => {
          const [key] = query.queryKey;
          return typeof key === "string" && key.includes("/api/data-source/v1");
        },
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "delete-data-source-failed",
        message: "Item Deletion Failed",
        details:
          error?.response?.data || "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

/**
 * Makes a data source read-only (3G-2d). One way: nothing in the product makes it writable again. The server
 * checks owner or admin, so a reader who is neither gets the refusal's own message back.
 */
export const useDemoteDataSource = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.post(`/api/data-source/v1/${id}/demote`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "demote-data-source",
        message: "Data source is now read-only",
        details: "Runs on agent run environments can now read from it",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        predicate: query => {
          const [key] = query.queryKey;
          return typeof key === "string" && key.includes("/api/data-source/v1");
        },
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "demote-data-source-failed",
        message: "Could not make the data source read-only",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
